import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface EmpresaType {
    id: number
    name: string;
    email: string;
    agencia?: string;
    contaBeneficiario?: string;
    User: {
        Id: number;
    }
}

export interface CreateEmpresaType {
    name: string;
    email: string;
    agencia?: string;
    contaBeneficiario?: string;
    password: string;
}

export interface EditEmpresaType {
    id: number;
    name: string;
    email: string;
    agencia?: string;
    contaBeneficiario?: string;
    password: string;
}

export default function useEmpresa() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listEmpresa, setListEmpresa] = useState<EmpresaType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [initialListEmpresa, setInitialListEmpresa] = useState<EmpresaType[]>([]);
    const [empresa, setEmpresa] = useState<EmpresaType>({
        id: 0,
        name: "",
        email: "",
        agencia: "",
        contaBeneficiario: "",
        User: {
            Id: 0,
        }
    });

    function createEmpresa(createEmpresaType: CreateEmpresaType): Promise<CreateEmpresaType | null> {
        setLoading(true);
        return api
            .post(`/Empresa/create`, createEmpresaType)
            .then((response) => {
                const createdEmpresa: CreateEmpresaType = response.data;
                showToast("success", "Empresa criada com sucesso.");
                setLoading(false);
                return createdEmpresa;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar Empresa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar Empresa:", error);
                return null;
            });
    }

    function getEmpresaPaged(
        pageNumber: number,
        Name?: string,
        pageSize?: number,
    ): Promise<EmpresaType[] | null> {
        setLoading(true);
        let url = `/Empresa/getEmpresaPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (Name) {
            url += `&Name=${Name}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListEmpresa(response.data.result);
                setInitialListEmpresa(response.data.result);
                setPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar Empresas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar Empresas:", error);
                return null;
            });
    }

    function updateEmpresa(updateEmpresaData: EditEmpresaType): Promise<EditEmpresaType | null> {
        setLoading(true);
        return api
            .put(`/Empresa/${updateEmpresaData.id}`, updateEmpresaData)
            .then((response) => {
                showToast("success", "Empresa atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar Empresa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar Empresa:", error);
                return null;
            });
    }

    function deleteEmpresa(EmpresaId: number): Promise<EmpresaType | null> {
        setLoading(true);
        return api
            .delete(`/Empresa/${EmpresaId}`)
            .then(() => {
                setEmpresa({
                    id: 0,
                    name: "",
                    email: "",
                    User: {
                        Id: 0,
                    }
                });
                showToast("success", "Empresa deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar Empresa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar Empresa:", error);
                return null;
            });
    }

    function handleGetEmpresaByUserId(userId: number): Promise<EmpresaType | null> {
        setLoading(true);
        return api
            .get(`/Empresa/getEmpresaByUserId/${userId}`)
            .then((response) => {
                const empresa: EmpresaType = response.data;
                setEmpresa(empresa);
                setLoading(false);
                return empresa;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações do Empresa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações do Empresa:", error);
                return null;
            });
    }
    
    function handleGetEmpresaById(empresaId: number): Promise<EmpresaType | null> {
        setLoading(true);
        return api
            .get(`/Empresa/${empresaId}`)
            .then((response) => {
                const empresa: EmpresaType = response.data;
                setEmpresa(empresa);
                setLoading(false);
                return empresa;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar Empresa pelo ID. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar Empresa pelo ID:", error);
                return null;
            });
    }   

    return { 
        createEmpresa,
        getEmpresaPaged,
        updateEmpresa,
        deleteEmpresa,
        handleGetEmpresaByUserId,
        handleGetEmpresaById,
        empresa,
        setEmpresa,
        listEmpresa,
        pageCount,
        initialListEmpresa,
    };
}